import React, { useState } from "react"
import styled from "styled-components"
import { Form } from "react-final-form"
import { FORM_ERROR } from "final-form"

import Grid, { Col } from "components/grid"
import { Box, Flex } from "components/box"
import { H3, Paragraph, ParagraphLarge } from "components/text"
import { PrimaryButton, TextButton } from "components/buttons"
import { BackLink } from "components/text/links/back-link"
import { BorderBox } from "components/page-elements"
import { MainContainer, FlexFormWrapper } from "components/main-container"
import {
  TextInputField,
  PhoneNumberInputField,
  DropdownInputField,
  DateInputField,
  validateDateOfBirth
} from "components/form"

import { useStates } from "graphql/queries/states"
import { useAssertValidRootQuery } from "graphql/smart-queries/user"
import { useUpdateUser } from "graphql/mutations/updateUser"

import { CardElement } from "shared/stripe/card-element"
import { StripeProvider, useStripe } from "shared/stripe/provider"
import validateAddress from "shared/validators/address"
import { transformZipCode } from "components/form/shared/transforms"

import { baseRoutes } from "routes"
import { UpdateUserMutationVariables } from "typings/types"

const BorderContainer = styled(BorderBox).attrs({
  py: [2, 4, 6, 6],
  px: [2, 4, 6, 8],
  mb: [0, 0, 5, 5],
  bg: "white"
})``

const FlexWrapper = styled(Flex)`
  flex-direction: row;
  justify-content: flex-start;
  flex-wrap: wrap;
`

const FieldContainer = styled(Box)``
FieldContainer.defaultProps = {
  width: ["100%", "100%", "50%", "50%"],
  p: 4
}

const Title = styled(ParagraphLarge).attrs({
  textAlign: "left",
  pl: 4,
  bold: true
})``

function UserEdit() {
  const [formState, setFormState] = useState<{ isEditPaymentMode?: boolean; loading?: boolean; success?: boolean }>({})
  const {
    user: { firstName, lastName, nameOnCard, email, profile, phone, address, stripeCards, hasEaglePatientId }
  } = useAssertValidRootQuery()
  const { data: statesData } = useStates()
  const [updateUser] = useUpdateUser({
    onError: () => setFormState({ loading: false }),
    onCompleted: () => setFormState({ success: true, loading: false })
  })
  const stripe = useStripe()

  if (!stripe) {
    throw new Error("Unexpected: stripe context is not set.")
  }

  const initialValues = {
    firstName,
    lastName,
    nameOnCard,
    email,
    dob: profile && profile.dob,
    phone: phone && `${phone.phoneAreaCode}${phone.phoneNumber}`,
    streetAddress1: (address && address.streetAddress1) || "",
    streetAddress2: (address && address.streetAddress2) || "",
    state: (address && address.state) || "",
    city: (address && address.city) || "",
    zipCode: (address && address.zipCode) || "",
    billingZip: stripeCards && stripeCards[0] && stripeCards[0].addressZip
  }

  const onSubmit = async (values: typeof initialValues) => {
    setFormState({ loading: true, isEditPaymentMode: formState.isEditPaymentMode })

    const errors = await validateAddress(values)
    if (Object.keys(errors).length > 0) {
      setFormState({ loading: false, isEditPaymentMode: formState.isEditPaymentMode })
      return errors
    }

    const userAttributes: UpdateUserMutationVariables = {
      params: {
        firstName: values.firstName,
        lastName: values.lastName,
        nameOnCard: values.nameOnCard,
        email: values.email,
        phoneAttributes: {
          phoneAreaCode: values.phone && values.phone.slice(0, 3),
          phoneNumber: values.phone && values.phone.slice(3, 10)
        },
        addressAttributes: {
          streetAddress1: values.streetAddress1,
          streetAddress2: values.streetAddress2,
          city: values.city,
          state: values.state,
          zipCode: values.zipCode
        },
        profileAttributes: {
          dob: values.dob
        }
      }
    }

    let stripeToken: string | undefined
    if (formState.isEditPaymentMode) {
      try {
        stripeToken = await prepareStripeToken(values.nameOnCard, values.billingZip!)
      } catch (e) {
        setFormState({ loading: false, isEditPaymentMode: formState.isEditPaymentMode })
        return { [FORM_ERROR]: e }
      }
    }

    const result = await updateUser({
      variables: {
        ...userAttributes,
        stripeToken
      }
    })

    if (result.error) {
      return result.error
    }

    setFormState({ success: !!(result && result.data && result.data.updateUser) })
    return {}
  }

  const prepareStripeToken = async (name: string, zip: string) => {
    const tokenDetails = {
      name,
      address_zip: zip,
      currency: "usd"
    }

    const response = await stripe.createToken(tokenDetails)

    if (response.error) {
      throw response.error.message
    }

    if (!response.token) {
      throw "The card could not be verified."
    }

    return response.token.id
  }

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, submitError }) => (
        <MainContainer
          onSubmit={handleSubmit}
          noValidate
          pageWrapperComponent={FlexFormWrapper}
          mainContent={
            <Grid rowProps={{ justifyContent: "center" }} pt={4} pb={[4, 4, 9, 9]} noGutter>
              <Col cols={[4, 4, 10, 10]}>
                <Box my={5}>
                  <BackLink to={baseRoutes.dashboard()} text={<Paragraph bold>Dashboard</Paragraph>} />
                </Box>
                <H3 textAlign="center" my={5}>
                  Account Settings
                </H3>
              </Col>
              <Col cols={[4, 4, 10, 10]}>
                <BorderContainer>
                  <Title>Personal Information</Title>
                  <FlexWrapper>
                    <FieldContainer>
                      <TextInputField name="firstName" label="First Name" disabled={hasEaglePatientId} required />
                    </FieldContainer>
                    <FieldContainer>
                      <TextInputField name="lastName" label="Last Name" disabled={hasEaglePatientId} required />
                    </FieldContainer>
                    <FieldContainer>
                      <PhoneNumberInputField name="phone" label="Phone Number" required />
                    </FieldContainer>
                    <FieldContainer>
                      <TextInputField name="email" label="Email" type="email" required />
                    </FieldContainer>
                    <FieldContainer>
                      <DateInputField
                        name="dob"
                        label="Date of Birth"
                        disabled={hasEaglePatientId}
                        validate={validateDateOfBirth}
                        required
                      />
                    </FieldContainer>
                  </FlexWrapper>
                </BorderContainer>

                <BorderContainer>
                  <Title>Shipping Information</Title>
                  <FlexWrapper>
                    <FieldContainer width="100%">
                      <TextInputField name="streetAddress1" label="Address 1" required />
                    </FieldContainer>

                    <FieldContainer width="100%">
                      <TextInputField name="streetAddress2" label="Address 2" />
                    </FieldContainer>

                    <FieldContainer width="100%">
                      <TextInputField name="city" label="City" required />
                    </FieldContainer>

                    <FieldContainer>
                      <DropdownInputField
                        name="state"
                        placeholder="State"
                        customProps={{
                          options: statesData && statesData.states ? statesData.states : []
                        }}
                        required
                      />
                    </FieldContainer>

                    <FieldContainer>
                      <TextInputField
                        name="zipCode"
                        label="Zipcode"
                        required
                        parse={transformZipCode}
                        type="tel"
                        pattern="[0-9]*"
                      />
                    </FieldContainer>
                  </FlexWrapper>
                </BorderContainer>

                <BorderContainer>
                  <Title>Payment Information</Title>
                  <FlexWrapper>
                    {formState.isEditPaymentMode ? (
                      <>
                        <FieldContainer width="100%">
                          <TextInputField name="nameOnCard" label="Name on Card" required />
                        </FieldContainer>

                        <FieldContainer width="100%">
                          <CardElement />
                        </FieldContainer>

                        <FieldContainer width={["100%", "100%", "50%"]}>
                          <TextInputField
                            name="billingZip"
                            label="Billing zip"
                            required
                            parse={transformZipCode}
                            type="tel"
                            pattern="[0-9]*"
                          />
                        </FieldContainer>
                      </>
                    ) : (
                      <Box pl={4}>
                        <Paragraph textAlign="left" mt={4} mb={3}>
                          {stripeCards && stripeCards.length > 0
                            ? `${stripeCards[0].brand} ending in ${stripeCards[0].last4}`
                            : "No card found"}
                        </Paragraph>
                        <TextButton
                          onClick={() => setFormState({ isEditPaymentMode: true })}
                          color="evergreen"
                          type="button"
                          fontWeight="normal"
                        >
                          Change payment method
                        </TextButton>
                      </Box>
                    )}
                  </FlexWrapper>
                </BorderContainer>

                <Flex justifyContent="center">
                  {submitError && <Paragraph color="salmon">{submitError}</Paragraph>}
                </Flex>

                <Flex justifyContent="center" mt={3}>
                  <PrimaryButton sizing="lg" disabled={formState.loading} loading={formState.loading}>
                    {formState.success ? "Saved!" : "Save"}
                  </PrimaryButton>
                </Flex>
              </Col>
            </Grid>
          }
        />
      )}
    />
  )
}

const UserEditWithStripe = () => (
  <StripeProvider>
    <UserEdit />
  </StripeProvider>
)
export default UserEditWithStripe
